import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import { WorkflowCartRow } from '@/components/shared/workflows/WorkflowCartRow';
import type { TurnInSummaryItem } from './types';
import { usePersonLabels } from '@/hooks/usePersonLabels';

export interface TurnInSidebarSummaryProps {
  serviceableItems: TurnInSummaryItem[];
  nonServiceableItems: TurnInSummaryItem[];
  hasItems: boolean;
  signatureAcknowledged: boolean;
  signaturePreviewSrc?: string;
  isSubmitting?: boolean;
  onShowCustomerSummary?: () => void;
  onRemoveItem: (id: string) => void;
  onCaptureSignature: () => void;
  onSkipSignature: () => void;
  onClearSignature?: () => void;
  onCompleteTurnIn: () => void;
}

const renderSummarySection = (
  items: TurnInSummaryItem[],
  emptyMessage: string,
  onRemoveItem: (id: string) => void,
) => {
  const groupedItems = new Map<string, TurnInSummaryItem[]>();
  items.forEach((item) => {
    const key = `${item.name}|${item.source}`;
    const group = groupedItems.get(key) ?? [];
    group.push(item);
    groupedItems.set(key, group);
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, maxHeight: '18vh', overflow: 'auto' }}>
      <Stack spacing={1.5}>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            {emptyMessage}
          </Typography>
        ) : (
          Array.from(groupedItems.entries()).map(([key, group]) => {
            const firstItem = group[0];
            if (!firstItem) return null;
            return (
              <WorkflowCartRow
                key={key}
                title={`${firstItem.name} (${group.length})`}
                subtitle={firstItem.source}
                actions={
                  <IconButton
                    size="small"
                    onClick={() => onRemoveItem(firstItem.id)}
                    aria-label={`Remove one ${firstItem.name}`}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                }
              />
            );
          })
        )}
      </Stack>
    </Paper>
  );
};

export const TurnInSidebarSummary = ({
  serviceableItems,
  nonServiceableItems,
  hasItems,
  signatureAcknowledged,
  signaturePreviewSrc,
  isSubmitting = false,
  onShowCustomerSummary,
  onRemoveItem,
  onCaptureSignature,
  onSkipSignature,
  onClearSignature,
  onCompleteTurnIn,
}: TurnInSidebarSummaryProps) => {
  const person = usePersonLabels('front_window');
  return (
    <Stack spacing={3} sx={{ pb: { xs: 0, md: 4 } }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Serviceable Cart
        </Typography>
        {renderSummarySection(serviceableItems, 'No serviceable items added', onRemoveItem)}
      </Stack>

      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Non-Serviceable Cart
        </Typography>
        {renderSummarySection(nonServiceableItems, 'No non-serviceable items added', onRemoveItem)}
      </Stack>

      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Summary
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Line Items Total
            </Typography>
            <Typography variant="body2">Serviceable: {serviceableItems.length}</Typography>
            <Typography variant="body2">Non-Serviceable: {nonServiceableItems.length}</Typography>
          </Stack>
        </Paper>
      </Stack>

      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Step 1 • Signature
        </Typography>
        {onShowCustomerSummary ? (
          <Button
            variant="outlined"
            fullWidth
            onClick={onShowCustomerSummary}
            disabled={!hasItems || isSubmitting}
          >
            View Turn-In Summary
          </Button>
        ) : null}
        <Button variant="outlined" fullWidth onClick={onCaptureSignature} disabled={!hasItems || isSubmitting}>
          {signatureAcknowledged && signaturePreviewSrc
            ? 'Signature Captured'
            : signatureAcknowledged
              ? 'Signature Skipped'
              : `Capture ${person.label} Signature`}
        </Button>
        {!signatureAcknowledged && (
          <Button
            variant="text"
            size="small"
            fullWidth
            onClick={onSkipSignature}
            disabled={!hasItems || isSubmitting}
            sx={{ mt: -0.5 }}
          >
            Skip Signature
          </Button>
        )}
        {signaturePreviewSrc ? (
          <Stack spacing={0.5}>
            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#ffffff' }}>
              <Box
                component="img"
                src={signaturePreviewSrc}
                alt="Captured signature preview"
                sx={{
                  display: 'block',
                  width: '100%',
                  maxHeight: 120,
                  objectFit: 'contain',
                }}
              />
            </Paper>
            {onClearSignature ? (
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={onClearSignature}
                sx={{ alignSelf: 'flex-start' }}
              >
                Clear signature
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Step 2 • Complete Transaction
        </Typography>
        <Tooltip
          title={
            !hasItems
              ? 'Add items to the cart first'
              : !signatureAcknowledged
                ? 'Complete Step 1 (Signature) first'
                : ''
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={onCompleteTurnIn}
              disabled={!hasItems || !signatureAcknowledged || isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Complete Transaction'}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
